import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { list_registrations, useEventsRequest } from './events_api';
import { RUN_STATUS_FILTERS } from './runs_table';
import { IST_ZONE } from './ist_time';
import { csv_of, download_csv } from './registrations_csv';

/**
 * Registrations tab — read-only, filtered by run status. "All" means every
 * registration on the topic, so the endpoint (which is per-run) is called once per
 * run in scope.
 *
 * Deliberately narrow: the underlying table carries UTMs, click ids, pixel cookies, IP
 * and user agent, and the API — so the export too — returns only the six fields.
 */

const COLUMNS = '1.3fr 1.9fr 1.1fr 1.2fr 1fr 1.3fr';
const HEADINGS = ['Name', 'Email', 'Phone', 'Timezone', 'City', 'Registered'];
const ROW_MIN = 820;

/** Stored UTC, shown in IST, which is the frame the ops team works in. */
const asIST = (iso) => moment(iso).tz(IST_ZONE).format('D MMM YYYY, h:mm A');
const run_label = (item) => moment(item.run_date, 'YYYY-MM-DD').format('ddd D MMM YYYY');
const slot_label = (iso) => moment(iso).tz(IST_ZONE).format('h:mm A');

/**
 * How many rows this screen will pull for a single run before it stops asking.
 *
 * The endpoint is paginated, so without a ceiling a run with tens of thousands of
 * registrations would page forever and put every row in the DOM. Grouping by slot
 * needs a whole run to be present for a group's count to match its rows, which is
 * why this pages to the end rather than showing one page — but it stops somewhere,
 * and says so when it does.
 */
const MAX_ROWS_PER_RUN = 2000;

export default function RegistrationsTab({ topic }) {
  const runs = useMemo(() => topic.runs || [], [topic.runs]);
  const [status_filter, setStatusFilter] = useState('all');
  const [rows, setRows] = useState([]);
  // Server-side COUNT per slot, independent of how many rows have been fetched. Group
  // headers read this so a number is never a lie about what exists.
  const [slot_counts, setSlotCounts] = useState({});
  const [truncated_runs, setTruncatedRuns] = useState([]);
  const [failed_runs, setFailedRuns] = useState([]);
  // Spans the whole multi-run load. The request hook's own flag falls to false between
  // runs, and an empty screen shown then is indistinguishable from a real empty result.
  const [busy, setBusy] = useState(true);
  const load_id = useRef(0);
  const { run } = useEventsRequest();

  const filtered_runs = useMemo(
    () => (status_filter === 'all' ? runs : runs.filter((item) => item.status === status_filter)),
    [runs, status_filter]
  );

  const load = useCallback(async () => {
    // Each load carries a generation. Changing the filter starts a new one, and a load
    // that is no longer current must stop paging and must never paint — otherwise the
    // slower "All" load lands after the "Draft" one and rows contradict the dropdown.
    load_id.current += 1;
    const generation = load_id.current;
    const current = () => load_id.current === generation;

    setBusy(true);

    if (filtered_runs.length === 0) {
      setRows([]);
      setSlotCounts({});
      setTruncatedRuns([]);
      setFailedRuns([]);
      setBusy(false);
      return;
    }

    // Follows next_cursor to the end of one run, or to the ceiling. Returns the rows
    // plus that run's true per-slot counts, so the caller can tell "all of them" from
    // "as many as we will show" from "we could not ask".
    const load_run = async (item) => {
      const collected = [];
      let counts = {};
      let before;
      let failed = false;

      for (;;) {
        // Silent, because one toast per failed run would bury the screen — the banner
        // names them together instead.
        const { data } = await run(() => list_registrations(item.run_uuid, { before }), {
          silent: true
        });
        if (!current()) break;
        if (!data) {
          failed = true;
          break;
        }

        counts = data.data.slot_counts || counts;
        (data.data.registrations || []).forEach((entry) => collected.push(entry));

        before = data.data.next_cursor;
        if (!before || collected.length >= MAX_ROWS_PER_RUN) break;
      }

      return { item, collected, counts, failed, hit_ceiling: !failed && Boolean(before) };
    };

    const results = [];
    // Sequential rather than parallel: a topic can carry a lot of runs, and firing one
    // paging loop per run at once is what turned this screen into a thundering herd.
    for (const item of filtered_runs) {
      results.push(await load_run(item));
      if (!current()) return;
    }

    const merged = [];
    const counts = {};
    const truncated = [];
    const failed = [];

    results.forEach(({ item, collected, counts: run_counts, hit_ceiling, failed: run_failed }) => {
      collected.forEach((entry) => merged.push({ ...entry, run: item }));
      Object.assign(counts, run_counts);
      if (hit_ceiling) truncated.push(run_label(item));
      if (run_failed) failed.push(run_label(item));
    });

    merged.sort((a, b) => (a.registered_at < b.registered_at ? 1 : -1));
    setRows(merged);
    setSlotCounts(counts);
    setTruncatedRuns(truncated);
    setFailedRuns(failed);
    setBusy(false);
  }, [run, filtered_runs]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * One group per run+slot, each carrying its own rows and its own download.
   *
   * A live slot always shows, even at zero — "nobody yet" is as often the answer a
   * host wants as a number is. A CANCELLED one shows only if somebody registered
   * before it was cancelled: §1.7 keeps cancelled slots visible so those people are
   * not hidden, and at zero there is nobody to hide.
   */
  const slot_groups = useMemo(
    () =>
      filtered_runs
        .flatMap((item) =>
          (item.slots || []).map((slot) => {
            const loaded = rows.filter((entry) => entry.slot_uuid === slot.slot_uuid);
            // The server's count, falling back to what we hold if it is absent.
            const total = slot_counts[slot.slot_uuid] ?? loaded.length;

            return {
              key: slot.slot_uuid,
              run_label: run_label(item),
              slot_label: slot_label(slot.start_utc),
              label: `${run_label(item)} ${slot_label(slot.start_utc)}`,
              cancelled: item.status === 'cancelled' || slot.status === 'cancelled',
              rows: loaded,
              total,
              // Only when paging stopped short: the header then reads "N of M" so the
              // download button's count is never mistaken for the whole slot.
              partial: total > loaded.length
            };
          })
        )
        .filter((group) => !group.cancelled || group.total > 0),
    [filtered_runs, rows, slot_counts]
  );

  const as_record = (entry) => [
    entry.name,
    entry.email,
    entry.phone || '',
    entry.user_timezone || '',
    entry.city || '',
    asIST(entry.registered_at)
  ];

  const export_slot = (group) =>
    download_csv(
      `${topic.slug}-${group.label.replace(/[^\w]+/g, '-').toLowerCase()}.csv`,
      csv_of([HEADINGS, ...group.rows.map(as_record)])
    );

  if (runs.length === 0) {
    return (
      <div
        className="rounded-lg p-6 text-center"
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-default)',
          color: 'var(--text-muted)'
        }}
      >
        No runs yet, so nobody can have registered.
      </div>
    );
  }

  return (
    <div
      className="rounded-lg"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-default)',
        boxShadow: 'var(--shadow-card)'
      }}
    >
      <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-default)' }}>
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Registrations
          </h3>

          <select
            value={status_filter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by run status"
            className="px-2 py-1 rounded text-sm"
            style={{
              background: 'var(--input-bg)',
              border: '1px solid var(--input-border)',
              color: 'var(--input-text)'
            }}
          >
            {RUN_STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <span className="ml-auto text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
            {busy ? 'Loading…' : `${rows.length} loaded`} across {filtered_runs.length}{' '}
            {filtered_runs.length === 1 ? 'run' : 'runs'}
          </span>
        </div>

        {failed_runs.length > 0 && (
          <div
            className="mt-2 flex items-center gap-2 text-xs"
            style={{ color: 'var(--state-error-text)' }}
          >
            <span>
              {failed_runs.length} of {filtered_runs.length}{' '}
              {filtered_runs.length === 1 ? 'run' : 'runs'} failed to load ({failed_runs.join(', ')}
              ). Their slots read zero here, which is not a count — registrations may exist. Retry
              before acting on this screen.
            </span>
            <button
              type="button"
              className="underline font-medium"
              onClick={load}
              disabled={busy}
              style={{ color: 'var(--state-error-text)' }}
            >
              Retry
            </button>
          </div>
        )}

        {truncated_runs.length > 0 && (
          <div className="mt-2 text-xs" style={{ color: 'var(--state-warning-text)' }}>
            Showing the most recent {MAX_ROWS_PER_RUN.toLocaleString()} registrations for{' '}
            {truncated_runs.join(', ')}. Slot counts above are the real totals; a CSV holds only
            what is loaded.
          </div>
        )}
      </div>

      {filtered_runs.length === 0 ? (
        <div className="px-4 py-6 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
          No runs match this filter.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {/* One header for the table, then a band per run and slot carrying its
              own download. */}
          <div
            className="grid gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-wide"
            style={{
              gridTemplateColumns: COLUMNS,
              minWidth: ROW_MIN,
              color: 'var(--text-secondary)',
              background: 'var(--surface-sunken)',
              borderBottom: '1px solid var(--border-default)'
            }}
          >
            {HEADINGS.map((heading) => (
              <div key={heading}>{heading}</div>
            ))}
          </div>

          {slot_groups.map((group) => (
            <div key={group.key}>
              <div
                className="flex items-center gap-3 px-4 py-2 text-sm"
                style={{
                  minWidth: ROW_MIN,
                  background: 'var(--surface-card)',
                  borderBottom: '1px solid var(--border-default)'
                }}
              >
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {group.run_label}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>{group.slot_label}</span>
                {group.cancelled && (
                  <span className="text-xs" style={{ color: 'var(--state-error-text)' }}>
                    cancelled
                  </span>
                )}
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {group.partial
                    ? `${group.rows.length} of ${group.total} registrations`
                    : `${group.total} ${group.total === 1 ? 'registration' : 'registrations'}`}
                </span>

                <button
                  type="button"
                  className="ml-auto"
                  onClick={() => export_slot(group)}
                  disabled={group.rows.length === 0}
                  title={
                    group.rows.length === 0
                      ? 'Nobody registered for this slot'
                      : `Download the ${group.rows.length} loaded row(s) as CSV`
                  }
                  style={{
                    background: group.rows.length === 0 ? 'transparent' : 'var(--surface-sunken)',
                    border: `1px solid ${
                      group.rows.length === 0 ? 'transparent' : 'var(--border-default)'
                    }`,
                    borderRadius: 4,
                    padding: '2px 8px',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    color: group.rows.length === 0 ? 'var(--text-muted)' : 'var(--state-info)',
                    cursor: group.rows.length === 0 ? 'default' : 'pointer'
                  }}
                >
                  {group.rows.length === 0 ? '\u2014' : '\u2193 Download CSV'}
                </button>
              </div>

              {group.rows.map((entry) => (
                <div
                  key={entry.registration_uuid}
                  className="grid gap-3 px-4 py-2 text-sm"
                  style={{
                    gridTemplateColumns: COLUMNS,
                    minWidth: ROW_MIN,
                    borderBottom: '1px solid var(--border-default)',
                    color: 'var(--text-primary)'
                  }}
                >
                  {/* Every visitor-supplied cell breaks anywhere. A 255-character name
                      with no spaces has no break opportunity, so without this it widens
                      its grid column past the viewport and stretches the whole row. */}
                  <div style={{ overflowWrap: 'anywhere' }}>{entry.name}</div>
                  <div style={{ overflowWrap: 'anywhere' }}>{entry.email}</div>
                  <div>{entry.phone || '\u2014'}</div>
                  {/* Their own IANA zone, uncoverted: it says where they are. */}
                  <div style={{ color: 'var(--text-secondary)' }}>
                    {entry.user_timezone || '\u2014'}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
                    {entry.city || '\u2014'}
                  </div>
                  <div style={{ color: 'var(--text-secondary)' }}>{asIST(entry.registered_at)}</div>
                </div>
              ))}
            </div>
          ))}

          {!busy && slot_groups.length === 0 && (
            <div className="px-4 py-6 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
              Nothing to show — these runs have no live slots, and no cancelled slot has a
              registration.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

RegistrationsTab.propTypes = {
  topic: PropTypes.shape({
    runs: PropTypes.arrayOf(
      PropTypes.shape({
        run_uuid: PropTypes.string,
        run_date: PropTypes.string,
        status: PropTypes.string,
        slots: PropTypes.arrayOf(PropTypes.shape({ slot_uuid: PropTypes.string }))
      })
    )
  }).isRequired
};
